/**
 * useAutosave — offline detection, IndexedDB recovery, navigation guard.
 *
 * Extracted from EstadisticasNew.js:
 *  - isOnline / isSaving / saveFailed state
 *  - The online/offline event listeners (window.addEventListener('online'/'offline'))
 *  - The IndexedDB recovery flow on mount (actionQueue.getGameState → recoveryDialog)
 *  - The beforeunload guard
 *  - window.estadisticasConfirmNavigation parent-driven navigation interception
 *  - persistGameState helper (was an inline useEffect)
 *  - The exit confirm dialog state + pendingNavigationRef plumbing
 *
 * Persistence to backend (handleSaveGameAndActions) is intentionally NOT in
 * here — it's a single big function that the screen layer composes from this
 * hook + useStatsSession + usePlayerRoster.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import actionQueue from '../../../utils/actionQueue';
import { STATS_MODE } from '../constants';

export function useAutosave({
  groupeId,
  t,
  onMessage,
  statsMode,
  gameInProgress,
  actionsLog,
}) {
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const [recoveryDialogOpen, setRecoveryDialogOpen] = useState(false);
  const [recoveryData, setRecoveryData] = useState(null);

  const [openExitConfirmDialog, setOpenExitConfirmDialog] = useState(false);
  const pendingNavigationRef = useRef(null);

  const message = useCallback((msg, severity = 'info') => {
    if (typeof onMessage === 'function') onMessage(msg, severity);
  }, [onMessage]);

  // ---------------------------------------------------------------------------
  // Online / offline tracking
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      message(t('estadisticasNew.connectionRestored'), 'success');
    };
    const handleOffline = () => {
      setIsOnline(false);
      message(t('estadisticasNew.connectionLost'), 'error');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [t, message]);

  // ---------------------------------------------------------------------------
  // Recovery on mount: ask the user if a previous session was found
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!groupeId) return;
    actionQueue.getGameState(groupeId).then((saved) => {
      if (saved && saved.state && saved.state.gameInProgress) {
        setRecoveryData(saved);
        setRecoveryDialogOpen(true);
      }
    }).catch((err) => {
      console.error('Error reading recovery state:', err);
    });
  }, [groupeId]);

  // ---------------------------------------------------------------------------
  // beforeunload — block tab/window close when there's unsaved data
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const unsaved =
        (gameInProgress && statsMode === STATS_MODE.LIVE) ||
        (statsMode === STATS_MODE.VIDEO && actionsLog.length > 0);

      if (unsaved) {
        e.preventDefault();
        e.returnValue = t('estadisticasNew.exitWarning');
        return e.returnValue;
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [gameInProgress, statsMode, actionsLog.length, t]);

  // ---------------------------------------------------------------------------
  // Parent-driven navigation interception
  // The cantera tab host calls window.estadisticasConfirmNavigation(cb) before
  // routing away. We answer:
  //   - true  → navigation OK
  //   - false → blocked, pending callback stored, exit dialog opened
  // ---------------------------------------------------------------------------
  const hasUnsavedData = useCallback(() => {
    return (
      (gameInProgress && statsMode === STATS_MODE.LIVE) ||
      (statsMode === STATS_MODE.VIDEO && actionsLog.length > 0) ||
      isSaving
    );
  }, [gameInProgress, statsMode, actionsLog.length, isSaving]);

  useEffect(() => {
    window.estadisticasConfirmNavigation = (callback) => {
      if (hasUnsavedData()) {
        pendingNavigationRef.current = callback;
        setOpenExitConfirmDialog(true);
        return false;
      }
      return true;
    };
    return () => {
      delete window.estadisticasConfirmNavigation;
    };
  }, [hasUnsavedData]);

  // ---------------------------------------------------------------------------
  // IndexedDB persistence
  // ---------------------------------------------------------------------------

  /**
   * Persist a snapshot of the game state to IndexedDB. Caller must compose
   * the snapshot itself (this hook does not own all the slices).
   */
  const persistGameState = useCallback((stateSnapshot) => {
    if (!groupeId) return;
    actionQueue.saveGameState(groupeId, stateSnapshot);
  }, [groupeId]);

  const clearGameState = useCallback(() => {
    if (groupeId) actionQueue.clearGameState(groupeId);
  }, [groupeId]);

  /**
   * Caller invokes this when the user clicks "Restore" in the recovery dialog.
   * Returns the saved state slice so the screen can rehydrate its hooks.
   */
  const acceptRecovery = useCallback(() => {
    const s = recoveryData?.state || null;
    setRecoveryDialogOpen(false);
    setRecoveryData(null);
    return s;
  }, [recoveryData]);

  const discardRecovery = useCallback(() => {
    if (groupeId) actionQueue.clearGameState(groupeId);
    setRecoveryDialogOpen(false);
    setRecoveryData(null);
  }, [groupeId]);

  // ---------------------------------------------------------------------------
  // Exit confirmation flow
  // ---------------------------------------------------------------------------

  /**
   * Programmatically request an exit confirmation. Pass a callback to be
   * executed if the user confirms (e.g. resetComponent or a navigation).
   */
  const requestExitConfirmation = useCallback((callback) => {
    pendingNavigationRef.current = callback || null;
    setOpenExitConfirmDialog(true);
  }, []);

  const confirmExit = useCallback(() => {
    setOpenExitConfirmDialog(false);
    const callback = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    if (typeof callback === 'function') callback();
  }, []);

  const cancelExit = useCallback(() => {
    setOpenExitConfirmDialog(false);
    pendingNavigationRef.current = null;
  }, []);

  return {
    // status
    isOnline,
    isSaving,
    setIsSaving,
    saveFailed,
    setSaveFailed,
    hasUnsavedData,

    // recovery
    recoveryDialogOpen,
    recoveryData,
    acceptRecovery,
    discardRecovery,

    // exit dialog
    openExitConfirmDialog,
    requestExitConfirmation,
    confirmExit,
    cancelExit,

    // persistence
    persistGameState,
    clearGameState,
  };
}
